import { Injectable } from '@angular/core';
import { StorageKey } from '../../enums/storage-enum';
import { StepResult } from '../../../modules/program/utilities/step-result.enum';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  // User & Authentication

  getUser(): any | null {
    const user = localStorage.getItem(StorageKey.User);
    return user ? JSON.parse(user) : null;
  }

  setUser(user: any): void {
    localStorage.setItem(StorageKey.User, JSON.stringify(user));
  }

  removeUser(): void {
    localStorage.removeItem(StorageKey.User);
  }

  getToken(): string | null {
    const token = localStorage.getItem(StorageKey.Token);
    return token ? JSON.parse(token) : null;
  }

  setToken(token: string): void {
    localStorage.setItem(StorageKey.Token, JSON.stringify(token));
  }

  removeToken(): void {
    localStorage.removeItem(StorageKey.Token);
  }

  getRole(): string | null {
    return localStorage.getItem(StorageKey.Role);
  }

  setRole(role: string): void {
    localStorage.setItem(StorageKey.Role, role);
  }

  removeRole(): void {
    localStorage.removeItem(StorageKey.Role);
  }

  // Session Management

  getSessionId(): string | null {
    return localStorage.getItem(StorageKey.SessionId);
  }

  setSessionId(sessionId: string): void {
    localStorage.setItem(StorageKey.SessionId, sessionId);
  }

  removeSessionId(): void {
    localStorage.removeItem(StorageKey.SessionId);
  }

  getLastEventTime(): string | null {
    return localStorage.getItem(StorageKey.LastEventTime);
  }

  setLastEventTime(time: string): void {
    localStorage.setItem(StorageKey.LastEventTime, time);
  }

  removeLastEventTime(): void {
    localStorage.removeItem(StorageKey.LastEventTime);
  }

  getAdminSession(): any | null {
    const session = localStorage.getItem(StorageKey.AdminSession);
    return session ? JSON.parse(session) : null;
  }

  setAdminSession(session: any): void {
    localStorage.setItem(StorageKey.AdminSession, JSON.stringify(session));
  }

  removeAdminSession(): void {
    localStorage.removeItem(StorageKey.AdminSession);
  }

  // Organization & Network

  getOrganization(): any | null {
    const org = localStorage.getItem(StorageKey.Organization);
    return org ? JSON.parse(org) : null;
  }

  setOrganization(organization: any): void {
    localStorage.setItem(StorageKey.Organization, JSON.stringify(organization));
  }

  removeOrganization(): void {
    localStorage.removeItem(StorageKey.Organization);
  }

  getIntermediary(): any | null {
    const intermediary = localStorage.getItem(StorageKey.Intermediary);
    return intermediary ? JSON.parse(intermediary) : null;
  }

  setIntermediary(intermediary: any): void {
    localStorage.setItem(StorageKey.Intermediary, JSON.stringify(intermediary));
  }

  removeIntermediary(): void {
    localStorage.removeItem(StorageKey.Intermediary);
  }

  // Program & Site

  getProgramId(): string | null {
    return localStorage.getItem(StorageKey.ProgramId);
  }

  setProgramId(id: string): void {
    localStorage.setItem(StorageKey.ProgramId, id);
  }

  removeProgramId(): void {
    localStorage.removeItem(StorageKey.ProgramId);
  }

  getSiteId(): string | null {
    return localStorage.getItem(StorageKey.SiteId);
  }

  setSiteId(id: string): void {
    localStorage.setItem(StorageKey.SiteId, id);
  }

  removeSiteId(): void {
    localStorage.removeItem(StorageKey.SiteId);
  }

  getProgramIdUrl(): string | null {
    return localStorage.getItem(StorageKey.ProgramIdUrl);
  }

  setProgramIdUrl(id: string): void {
    localStorage.setItem(StorageKey.ProgramIdUrl, id);
  }

  removeProgramIdUrl(): void {
    localStorage.removeItem(StorageKey.ProgramIdUrl);
  }

  getProgramData(): any | null {
    const data = localStorage.getItem(StorageKey.ProgramData);
    return data ? JSON.parse(data) : null;
  }

  setProgramData(data: any): void {
    localStorage.setItem(StorageKey.ProgramData, JSON.stringify(data));
  }

  removeProgramData(): void {
    localStorage.removeItem(StorageKey.ProgramData);
  }

  // Reports & Portfolio

  getReportType(): string | null {
    return localStorage.getItem(StorageKey.ReportType);
  }

  setReportType(type: string): void {
    localStorage.setItem(StorageKey.ReportType, type);
  }

  removeReportType(): void {
    localStorage.removeItem(StorageKey.ReportType);
  }

  getReportData(): any | null {
    const data = localStorage.getItem(StorageKey.ReportData);
    return data ? JSON.parse(data) : null;
  }

  setReportData(data: any): void {
    localStorage.setItem(StorageKey.ReportData, JSON.stringify(data));
  }

  removeReportData(): void {
    localStorage.removeItem(StorageKey.ReportData);
  }

  getPortfolioId(): string | null {
    return localStorage.getItem(StorageKey.PortfolioId);
  }

  setPortfolioId(id: string): void {
    localStorage.setItem(StorageKey.PortfolioId, id);
  }

  removePortfolioId(): void {
    localStorage.removeItem(StorageKey.PortfolioId);
  }

  getPortfolioName(): string | null {
    return localStorage.getItem(StorageKey.PortfolioName);
  }

  setPortfolioName(name: string): void {
    localStorage.setItem(StorageKey.PortfolioName, name);
  }

  removePortfolioName(): void {
    localStorage.removeItem(StorageKey.PortfolioName);
  }

  getPortfolioFilters(): any | null {
    const filters = localStorage.getItem(StorageKey.PortfolioFilters);
    return filters ? JSON.parse(filters) : null;
  }

  setPortfolioFilters(filters: any): void {
    localStorage.setItem(StorageKey.PortfolioFilters, JSON.stringify(filters));
  }

  removePortfolioFilters(): void {
    localStorage.removeItem(StorageKey.PortfolioFilters);
  }

  getPortfolioState(): any | null {
    const state = localStorage.getItem(StorageKey.PortfolioState);
    return state ? JSON.parse(state) : null;
  }

  setPortfolioState(state: any): void {
    localStorage.setItem(StorageKey.PortfolioState, JSON.stringify(state));
  }

  removePortfolioState(): void {
    localStorage.removeItem(StorageKey.PortfolioState);
  }

  getOldPortfolioName(): string | null {
    return localStorage.getItem(StorageKey.OldPortfolioName);
  }

  setOldPortfolioName(name: string): void {
    localStorage.setItem(StorageKey.OldPortfolioName, name);
  }

  removeOldPortfolioName(): void {
    localStorage.removeItem(StorageKey.OldPortfolioName);
  }

  getOldPortfolioFilters(): any | null {
    const filters = localStorage.getItem(StorageKey.OldPortfolioFilters);
    return filters ? JSON.parse(filters) : null;
  }

  setOldPortfolioFilters(filters: any): void {
    localStorage.setItem(
      StorageKey.OldPortfolioFilters,
      JSON.stringify(filters)
    );
  }

  removeOldPortfolioFilters(): void {
    localStorage.removeItem(StorageKey.OldPortfolioFilters);
  }

  getOldPortfolioState(): any | null {
    const state = localStorage.getItem(StorageKey.OldPortfolioState);
    return state ? JSON.parse(state) : null;
  }

  setOldPortfolioState(state: any): void {
    localStorage.setItem(StorageKey.OldPortfolioState, JSON.stringify(state));
  }

  removeOldPortfolioState(): void {
    localStorage.removeItem(StorageKey.OldPortfolioState);
  }

  getPortfolioFilterSelection(): any | null {
    const selection = localStorage.getItem(StorageKey.PortfolioFilterSelection);
    return selection ? JSON.parse(selection) : null;
  }

  setPortfolioFilterSelection(selection: any): void {
    localStorage.setItem(
      StorageKey.PortfolioFilterSelection,
      JSON.stringify(selection)
    );
  }

  removePortfolioFilterSelection(): void {
    localStorage.removeItem(StorageKey.PortfolioFilterSelection);
  }

  getOldPortfolioFilterSelection(): any | null {
    const selection = localStorage.getItem(
      StorageKey.OldPortfolioFilterSelection
    );
    return selection ? JSON.parse(selection) : null;
  }

  setOldPortfolioFilterSelection(selection: any): void {
    localStorage.setItem(
      StorageKey.OldPortfolioFilterSelection,
      JSON.stringify(selection)
    );
  }

  removeOldPortfolioFilterSelection(): void {
    localStorage.removeItem(StorageKey.OldPortfolioFilterSelection);
  }

  getOriginalReportName(): string | null {
    return localStorage.getItem(StorageKey.OriginalReportName);
  }

  setOriginalReportName(name: string): void {
    localStorage.setItem(StorageKey.OriginalReportName, name);
  }

  removeOriginalReportName(): void {
    localStorage.removeItem(StorageKey.OriginalReportName);
  }

  // Stage & Navigation

  getStage(): string | null {
    return localStorage.getItem(StorageKey.Stage);
  }

  setStage(stage: string): void {
    localStorage.setItem(StorageKey.Stage, stage);
  }

  removeStage(): void {
    localStorage.removeItem(StorageKey.Stage);
  }

  getCurrentToolName(): string | null {
    return localStorage.getItem(StorageKey.CurrentToolName);
  }

  setCurrentToolName(toolName: string): void {
    localStorage.setItem(StorageKey.CurrentToolName, toolName);
  }

  removeCurrentToolName(): void {
    localStorage.removeItem(StorageKey.CurrentToolName);
  }

  getCurrentNav(): string | null {
    return localStorage.getItem(StorageKey.CurrentNav);
  }

  setCurrentNav(nav: string): void {
    localStorage.setItem(StorageKey.CurrentNav, nav);
  }

  removeCurrentNav(): void {
    localStorage.removeItem(StorageKey.CurrentNav);
  }

  getPreviousUrl(): string | null {
    return localStorage.getItem(StorageKey.PreviousUrl);
  }

  setPreviousUrl(url: string): void {
    localStorage.setItem(StorageKey.PreviousUrl, url);
  }

  removePreviousUrl(): void {
    localStorage.removeItem(StorageKey.PreviousUrl);
  }

  getNextUrl(): string | null {
    return localStorage.getItem(StorageKey.NextUrl);
  }

  setNextUrl(url: string): void {
    localStorage.setItem(StorageKey.NextUrl, url);
  }

  removeNextUrl(): void {
    localStorage.removeItem(StorageKey.NextUrl);
  }

  getShowSelection(): string | null {
    return localStorage.getItem(StorageKey.ShowSelection);
  }

  setShowSelection(value: string): void {
    localStorage.setItem(StorageKey.ShowSelection, value);
  }

  removeShowSelection(): void {
    localStorage.removeItem(StorageKey.ShowSelection);
  }

  // Subscriptions & Features

  getSubscriptions(): any | null {
    const subscriptions = localStorage.getItem(StorageKey.Subscriptions);
    return subscriptions ? JSON.parse(subscriptions) : null;
  }

  setSubscriptions(subscriptions: any): void {
    localStorage.setItem(
      StorageKey.Subscriptions,
      JSON.stringify(subscriptions)
    );
  }

  removeSubscriptions(): void {
    localStorage.removeItem(StorageKey.Subscriptions);
  }

  getCheckinAllowed(): string | null {
    return localStorage.getItem(StorageKey.CheckinAllowed);
  }

  setCheckinAllowed(value: string): void {
    localStorage.setItem(StorageKey.CheckinAllowed, value);
  }

  removeCheckinAllowed(): void {
    localStorage.removeItem(StorageKey.CheckinAllowed);
  }

  getPrepostAllowed(): string | null {
    return localStorage.getItem(StorageKey.PrepostAllowed);
  }

  setPrepostAllowed(value: string): void {
    localStorage.setItem(StorageKey.PrepostAllowed, value);
  }

  removePrepostAllowed(): void {
    localStorage.removeItem(StorageKey.PrepostAllowed);
  }

  // Manage Program
  getManageProgramStep(): string | null {
    return localStorage.getItem(StorageKey.ManageProgramStep);
  }

  setManageProgramStep(step: string): void {
    localStorage.setItem(StorageKey.ManageProgramStep, step);
  }

  removeManageProgramStep(): void {
    localStorage.removeItem(StorageKey.ManageProgramStep);
  }

  getManageProgramGroupEditSite(): any | null {
    const site = localStorage.getItem(StorageKey.ManageProgramGroupEditSite);
    return site ? site : null;
  }

  setManageProgramGroupEditSite(site: any): void {
    localStorage.setItem(
      StorageKey.ManageProgramGroupEditSite,
      JSON.stringify(site)
    );
  }

  removeManageProgramGroupEditSite(): void {
    localStorage.removeItem(StorageKey.ManageProgramGroupEditSite);
  }

  getManageProgramGroupEdit(): any | null {
    const group = localStorage.getItem(StorageKey.ManageProgramGroupEdit);
    return group ? JSON.parse(group) : null;
  }

  setManageProgramGroupEdit(group: any): void {
    localStorage.setItem(
      StorageKey.ManageProgramGroupEdit,
      JSON.stringify(group)
    );
  }

  removeManageProgramGroupEdit(): void {
    localStorage.removeItem(StorageKey.ManageProgramGroupEdit);
  }

  getTrackingStep(): string | null {
    return localStorage.getItem(StorageKey.TrackingStep);
  }

  setTrackingStep(step: string): void {
    localStorage.setItem(StorageKey.TrackingStep, step);
  }

  removeTrackingStep(): void {
    localStorage.removeItem(StorageKey.TrackingStep);
  }

  getFrontendVersion(): string | null {
    return localStorage.getItem(StorageKey.FrontendVersion);
  }

  setFrontendVersion(version: string): void {
    localStorage.setItem(StorageKey.FrontendVersion, version);
  }

  removeFrontendVersion(): void {
    localStorage.removeItem(StorageKey.FrontendVersion);
  }

  getInitiativeSelected(): string | null {
    return localStorage.getItem(StorageKey.InitiativeSelected);
  }

  setInitiativeSelected(value: string): void {
    localStorage.setItem(StorageKey.InitiativeSelected, value);
  }

  removeInitiativeSelected(): void {
    localStorage.removeItem(StorageKey.InitiativeSelected);
  }

  getLocalInitiative(): any | null {
    const initiative = localStorage.getItem(StorageKey.LocalInitiative);
    return initiative ? JSON.parse(initiative) : null;
  }

  setLocalInitiative(initiative: any): void {
    localStorage.setItem(
      StorageKey.LocalInitiative,
      JSON.stringify(initiative)
    );
  }

  removeLocalInitiative(): void {
    localStorage.removeItem(StorageKey.LocalInitiative);
  }

  getFilters(): any | null {
    const filters = localStorage.getItem(StorageKey.Filters);
    return filters ? JSON.parse(filters) : null;
  }

  setFilters(filters: any): void {
    localStorage.setItem(StorageKey.Filters, JSON.stringify(filters));
  }

  removeFilters(): void {
    localStorage.removeItem(StorageKey.Filters);
  }

  getImpactSummariesAccessPoint(): string | null {
    return localStorage.getItem(StorageKey.ImpactSummariesAccessPoint);
  }

  setImpactSummariesAccessPoint(type: string): void {
    localStorage.setItem(StorageKey.ImpactSummariesAccessPoint, type);
  }

  removeImpactSummariesAccessPoint(): void {
    localStorage.removeItem(StorageKey.ImpactSummariesAccessPoint);
  }

  getAccessInviteReportViewer(): string | null {
    return localStorage.getItem(StorageKey.AccessInviteReportViewer);
  }

  setAccessInviteReportViewer(value: string): void {
    localStorage.setItem(StorageKey.AccessInviteReportViewer, value);
  }

  removeAccessInviteReportViewer(): void {
    localStorage.removeItem(StorageKey.AccessInviteReportViewer);
  }

  getToolName(): string | null {
    return localStorage.getItem(StorageKey.ToolName);
  }

  setToolName(name: string): void {
    localStorage.setItem(StorageKey.ToolName, name);
  }

  setStepResult(stepResult: StepResult): void {
    localStorage.setItem(StorageKey.StepResult, stepResult);
  }

  getStepResult(): string | null {
    return localStorage.getItem(StorageKey.StepResult);
  }

  removeToolName(): void {
    localStorage.removeItem(StorageKey.ToolName);
  }

  clear(): void {
    localStorage.clear();
  }

  clearSession(): void {
    this.removeUser();
    this.removeRole();
    this.removeOrganization();
    this.removeIntermediary();
    this.removeToken();
    this.removeAdminSession();
    this.removeSessionId();
  }
}
